import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, ArrowRight, CreditCard, BarChart, ClockIcon, Tag, Percent, DollarSign, Clock, X, Globe } from 'lucide-react';
import { ParticleBackground } from '../components/ParticleBackground';
import { SubscriptionPlans } from '../components/subscription/SubscriptionPlans';
import { SubscriptionUsage } from '../components/subscription/SubscriptionUsage';
import { AddOnBundles } from '../components/subscription/AddOnBundles';
import { PurchaseHistory } from '../components/subscription/PurchaseHistory';
import { useToast } from '../components/common/Toast';
import { Button } from '../components/common/Button';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import { useAuthStore } from '../store';
import { useAuth } from '../contexts/AuthContext';
// Import subscription service for any direct page-level needs
import subscriptionService, { CouponValidationResult } from '../services/subscriptionService';
import interviewService, { UserInterview } from '../services/interviewService'; // Add interview service import
import CouponInput from '../components/subscription/CouponInput';
import { locationService } from '../services/locationService';
import { CollegeAvatars } from '../components/common/CollegeAvatars';

// Define type for plan-specific discount
type PlanDiscount = {
  planId: string;
  discountedPrice: number;
  originalPrice: number;
  discountAmount: number;
  freeMinutes: number;
};

interface SubscriptionPageProps {
  fromSource: 'dashboard' | 'onboarding';
  subscriptionInfo: {
    planType: 'free' | 'monthly' | 'yearly' | 'quarterly';
    planName: string;
    isActive: boolean;
    expiryDate: string;
    minutesLeft: number;
    isUnlimited?: boolean;
  };
  isLoadingSubscription?: boolean;
  onSubscriptionUpdate?: () => void;
}


// Helper function to get date range for a month
function getDateRangeForMonth(date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0, 23, 59, 59, 999);
  
  return {
    fromDate: firstDay.toISOString(),
    toDate: lastDay.toISOString()
  };
}

// Helper function to get current month name with year
function getCurrentMonthName() {
  const date = new Date();
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
}

// Helper function to get last month name with year
function getLastMonthName() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
}

// Generate date filters for the last several months
const generateDateFilters = () => {
  const now = new Date();
  const filters = [];
  
  // Current month
  filters.push({ 
    value: 'this-month', 
    label: getCurrentMonthName(),
    fromDate: getDateRangeForMonth(now).fromDate,
    toDate: getDateRangeForMonth(now).toDate
  });
  
  // Previous month
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  filters.push({ 
    value: 'last-month', 
    label: getLastMonthName(),
    fromDate: getDateRangeForMonth(lastMonth).fromDate,
    toDate: getDateRangeForMonth(lastMonth).toDate
  });

  // Add aggregate time periods
  filters.push({
    value: 'last-3-months',
    label: 'Last 3 Months',
    fromDate: new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString(),
    toDate: now.toISOString()
  });
  
  filters.push({
    value: 'last-6-months',
    label: 'Last 6 Months',
    fromDate: new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString(),
    toDate: now.toISOString()
  });
  
  filters.push({
    value: 'last-12-months',
    label: 'Last 12 Months',
    fromDate: new Date(now.getFullYear(), now.getMonth() - 12, 1).toISOString(),
    toDate: now.toISOString()
  });
  
  // Add all-time option for payment history
  filters.push({
    value: 'all-time',
    label: 'All Time',
    // No from/to dates for all-time
  });
  
  return filters;
};

const SubscriptionPage: React.FC<SubscriptionPageProps> = React.memo(({ 
  fromSource,
  subscriptionInfo,
  isLoadingSubscription = false,
  onSubscriptionUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'plans' | 'usage' | 'history'>('plans');
  const [isTabChanging, setIsTabChanging] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  // Date filters for interview history
  const [dateFilters] = useState(generateDateFilters());
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('this-month');
  // State for the interview history data
  const [interviewHistoryData, setInterviewHistoryData] = useState<UserInterview[]>([]);
  const [isLoadingInterviewHistory, setIsLoadingInterviewHistory] = useState(false);
  const [interviewHistoryError, setInterviewHistoryError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const plansTabRef = useRef<HTMLDivElement>(null);
  const usageTabRef = useRef<HTMLDivElement>(null);
  const tabChangeTimeRef = useRef<number>(0);
  
  // Get user data from auth store and context as fallback
  const { user: storeUser } = useAuthStore();
  const { user: contextUser } = useAuth();
  
  // Use store user if available, otherwise fallback to context user
  const user = storeUser || contextUser;
  
  // Create user data with subscription info
  const userData = {
    name: user?.name || "-",
    profileImage: user?.profilePicture || (user as any)?.profileImage || "",
    subscriptionPlan: subscriptionInfo.planName,
    subscriptionExpiry: subscriptionInfo.expiryDate,
    remainingMinutes: subscriptionInfo.minutesLeft,
    activePlan: subscriptionInfo.planType as 'free' | 'monthly' | 'yearly' | 'quarterly',
    isUnlimited: subscriptionInfo.isUnlimited || false
  };
  
  // State for storing user subscription details
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const hasInitialInterviewFetch = React.useRef(false);
  
  // Add state for upcoming interviews
  const [upcomingInterviews, setUpcomingInterviews] = useState<UserInterview[]>([]);
  const [isLoadingUpcomingInterviews, setIsLoadingUpcomingInterviews] = useState(false);
  
  // Keep track of the last fetched date range to prevent flicker
  const lastFetchRef = useRef<{from: string; to: string} | null>(null);
  
  const [couponDetails, setCouponDetails] = useState<CouponValidationResult | null>(null);
  const [showStandaloneCoupon, setShowStandaloneCoupon] = useState<boolean>(false);
  const { showToast } = useToast();
  
  // Location detection states
  const [shouldShowPricing, setShouldShowPricing] = useState(true);
  const [userLocation, setUserLocation] = useState<{ country: string; countryCode: string } | null>(null);
  
  // Used to track ongoing discount calculations to prevent duplicate updates
  const pendingCalculationsRef = useRef<Set<string>>(new Set());
  const previousCouponDetailsRef = useRef<CouponValidationResult | null>(null);
  
  // Add state for initial coupon code from URL
  const [initialCouponCode, setInitialCouponCode] = useState<string | undefined>(undefined);
  const initialCouponSetRef = useRef(false);
  
  // Function to fetch upcoming interviews
  const fetchUpcomingInterviews = async () => {
    if (isLoadingUpcomingInterviews) {
      console.log('SubscriptionPage: Already loading upcoming interviews, skipping fetch');
      return;
    }
    
    try {
      setIsLoadingUpcomingInterviews(true);
      console.log('SubscriptionPage: Fetching upcoming interviews');
      
      // Fetch upcoming interviews using the existing method from interviewService
      const upcoming = await interviewService.getUpcomingInterviews(true);
      console.log(`SubscriptionPage: Fetched ${upcoming.length} upcoming interviews`);
      
      setUpcomingInterviews(upcoming);
    } catch (error) {
      console.error('SubscriptionPage: Error fetching upcoming interviews:', error);
    } finally {
      setIsLoadingUpcomingInterviews(false);
    }
  };
  
  // Function to fetch interview history - centralized here
  const fetchInterviewHistory = async () => {
    // Skip if we're already loading
    if (isLoadingInterviewHistory) {
      console.log('SubscriptionPage: Already loading interviews, skipping fetch');
      return;
    }
    
    try {
      // Get the current date filter
      const currentFilter = dateFilters.find(filter => filter.value === selectedDateFilter) || dateFilters[0];
      console.log('SubscriptionPage: Using date filter:', currentFilter);
      
      if (!currentFilter.fromDate || !currentFilter.toDate) {
        console.warn('SubscriptionPage: Date filter missing fromDate or toDate. Using default values.');
        
        // Fallback to current month if the filter doesn't have date range
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        
        currentFilter.fromDate = firstDay.toISOString();
        currentFilter.toDate = lastDay.toISOString();
      }
      
      // Check if we're fetching the same date range as before to prevent flickering UI
      const isSameDateRange = lastFetchRef.current && 
                              lastFetchRef.current.from === currentFilter.fromDate && 
                              lastFetchRef.current.to === currentFilter.toDate;
                              
      console.log(`SubscriptionPage: Fetching interviews with date range: from=${currentFilter.fromDate}, to=${currentFilter.toDate}`);
      
      // Only toggle loading state if the date range changed
      if (!isSameDateRange) {
        setIsLoadingInterviewHistory(true);
        setInterviewHistoryError(null);
      } else {
        console.log('SubscriptionPage: Using cached interview history for date range:', currentFilter.fromDate, 'to', currentFilter.toDate);
      }
      
      const interviews = await interviewService.getUserInterviewHistory(
        currentFilter.fromDate,
        currentFilter.toDate
      );
      
      // Update last fetch reference
      lastFetchRef.current = {
        from: currentFilter.fromDate,
        to: currentFilter.toDate
      };
      
      setInterviewHistoryData(interviews);
    } catch (error) {
      console.error('SubscriptionPage: Error fetching interview history:', error);
      setInterviewHistoryError('Failed to load interview history');
    } finally {
      setIsLoadingInterviewHistory(false);
      hasInitialInterviewFetch.current = true;
    }
  };
    
  // Fetch interview history when page mounts
  useEffect(() => {
    // Force immediate fetch regardless of the hasInitialInterviewFetch flag
    console.log('SubscriptionPage: Component mounted, forcing initial interview history fetch');
    hasInitialInterviewFetch.current = false; // Reset the flag
    fetchInterviewHistory();
    
    // Also fetch upcoming interviews
    fetchUpcomingInterviews();
    
    // Mark as fetched after this initial load
    return () => {
      hasInitialInterviewFetch.current = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run on every mount

  // Fetch interview history when date filter changes (separate from initial load)
  useEffect(() => {
    // Only fetch on filter changes if we've already done the initial fetch
    if (hasInitialInterviewFetch.current && !isLoadingInterviewHistory) {
      console.log(`SubscriptionPage: Filter changed to ${selectedDateFilter}, fetching data`);
      fetchInterviewHistory();
      
      // Also refresh upcoming interviews when date filter changes
      fetchUpcomingInterviews();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateFilter]);
  
  // Fetch user subscription data on component mount
  useEffect(() => {
    // Fetch subscription data if needed
    const fetchUserSubscription = async () => {
      if (userData.activePlan !== 'free') {
        try {
          const userSubData = await subscriptionService.getUserSubscription();
          setUserSubscription(userSubData);
          console.log('SubscriptionPage: Fetched user subscription details:', userSubData);
        } catch (err) {
          console.error('SubscriptionPage: Error fetching user subscription details:', err);
        }
      }
    };
    
    // Run subscription fetch on mount
    fetchUserSubscription();
    
    // Initial fetch of interview history (only needed on first mount)
    if (!hasInitialInterviewFetch.current) {
      fetchInterviewHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.activePlan]); // Only re-run when activePlan changes

  // Detect user location to determine pricing visibility
  useEffect(() => {
    const detectLocation = async () => {
      try {
        const showPricing = await locationService.shouldShowPricing();
        const location = await locationService.getLocationForTrialRequest();
        
        setShouldShowPricing(showPricing);
        setUserLocation(location);
        
        console.log('SubscriptionPage: Location detected:', location, 'Show pricing:', showPricing);
      } catch (error) {
        console.error('SubscriptionPage: Error detecting location:', error);
        // Fallback to showing pricing (India default)
        setShouldShowPricing(true);
        setUserLocation({ country: 'India', countryCode: 'IN' });
      }
    };

    detectLocation();
  }, []);
  
  // Handle date filter change (this is passed down to child components)
  const handleDateFilterChange = (filter: string) => {
    console.log('SubscriptionPage: Date filter changed to', filter);
    setSelectedDateFilter(filter);
    // The fetchInterviewHistory will be triggered by the useEffect
  };
  
  // Log user data for debugging
  useEffect(() => {
    console.log('SUBSCRIPTION PAGE - User data being used:', userData);
    console.log('SUBSCRIPTION PAGE - Original user sources:', { 
      storeUser,
      contextUser,
      userChosen: storeUser ? 'store' : contextUser ? 'context' : 'fallback'
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeUser, contextUser, subscriptionInfo, userData]);
  
  // Shorter tab labels for mobile
  const tabLabels = {
    plans: isMobileView ? 'Plans' : 'Subscription Plans',
    usage: isMobileView ? 'Usage' : 'Usage & History',
    history: isMobileView ? 'History' : 'Payment History'
  };
  
  // Check mobile view
  useEffect(() => {
    const checkMobileView = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;
      setIsMobileView(isMobile);
    };

    // Initialize on mount
    checkMobileView();
    
    // Set up ResizeObserver for more reliable detection
    const bodyResizeObserver = new ResizeObserver(() => {
      // We observe the body to detect viewport size changes
      checkMobileView();
    });
    
    bodyResizeObserver.observe(document.body);
    
    // Also listen for traditional events as fallback
    window.addEventListener('resize', checkMobileView);
    
    // Force a re-check after a short delay to handle some edge cases
    setTimeout(checkMobileView, 300);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
      bodyResizeObserver.disconnect();
    };
  }, []);
  
  // Check for tab parameter in URL - only on initial mount
  useEffect(() => {
    // Only check for tab parameter if not in onboarding flow
    if (fromSource !== 'onboarding') {
      const searchParams = new URLSearchParams(location.search);
      const tabParam = searchParams.get('tab');
      if (tabParam === 'usage') {
        setActiveTab('usage');
      } else if (tabParam === 'history') {
        setActiveTab('history');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debug effect for tab changes
  useEffect(() => {
    console.log(`Tab changed to: ${activeTab} at ${new Date().toISOString()}`);
    tabChangeTimeRef.current = performance.now();
    
    // Debug rendering times
    const checkRender = () => {
      const now = performance.now();
      const elapsed = now - tabChangeTimeRef.current;
      console.log(`Tab content rendered after ${elapsed.toFixed(2)}ms`);
      
      if (activeTab === 'plans' && plansTabRef.current) {
        console.log('Plans tab is visible:', plansTabRef.current.offsetHeight > 0);
      }
      if (activeTab === 'usage' && usageTabRef.current) {
        console.log('Usage tab is visible:', usageTabRef.current.offsetHeight > 0);
      }
      
      // Clear the tab changing flag after the tab content has been checked
      setIsTabChanging(false);
    };
    
    // Check after a frame render and after a short delay
    requestAnimationFrame(checkRender);
  }, [activeTab]);

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  const handleTabChange = (tab: 'plans' | 'usage' | 'history') => {
    // Prevent tab change if one is already in progress
    if (isTabChanging || tab === activeTab || fromSource === 'onboarding') {
      console.log(`Tab change to ${tab} skipped: ${isTabChanging ? 'another change in progress' : 'already active or in onboarding'}`);
      return;
    }
    
    console.log(`Tab change requested: ${tab} at ${new Date().toISOString()}`);
    setIsTabChanging(true);
    setActiveTab(tab);
    
    // Update URL without page reload
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);
    // We don't need to navigate anymore, just change the local state
    // The isTabChanging flag will be cleared by the activeTab useEffect
  };

  // Update the tab-based fetching to include upcoming interviews
  useEffect(() => {
    // Only fetch if:
    // 1. We're not in the middle of a tab change
    // 2. We're not already loading data
    // 3. Either we're on the usage tab, or we need to do the initial data load
    const shouldFetch = !isTabChanging && 
                        !isLoadingInterviewHistory && 
                        (activeTab === 'usage' || hasInitialInterviewFetch.current === false);
                        
    if (shouldFetch) {
      console.log(`SubscriptionPage: Fetching interview history due to tab change or initial load`);
      fetchInterviewHistory();
      fetchUpcomingInterviews();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isTabChanging, isLoadingInterviewHistory]);
  
  const handleContinueWithFreePlan = () => {
    // Navigate to the dashboard or another page after onboarding
    navigate('/dashboard');
  };

  // Handle coupon validation at the page level
  const handleCouponValidated = (isValid: boolean, details: any) => {
    console.log('Coupon validation result:', isValid, details);
    
    if (!details) {
      // Reset if no details provided
      setCouponDetails(null);
      setShowStandaloneCoupon(false);
      return;
    }
    
    // Deep equality check to prevent unnecessary updates
    const isSameCoupon = previousCouponDetailsRef.current && 
                         previousCouponDetailsRef.current.code === details.code &&
                         previousCouponDetailsRef.current.isValid === isValid;
    
    if (isSameCoupon) {
      console.log('SubscriptionPage: Skipping update for unchanged coupon details');
      return;
    }
    
    // Copy what we need from the details to prevent unnecessary state updates
    const basicDetails = {
      ...details,
      code: details.code,
      description: details.description,
      freeMinutes: details.freeMinutes || 0,
      couponId: details.couponId,
      discountType: details.discountType,
      discountAmount: details.discountAmount || 0,
      originalPrice: details.originalPrice || 0,
      discountedPrice: details.discountedPrice || 0,
      isValid,
      message: details.message || '',
      value: details.value,
      applicablePlans: details.applicablePlans,
      requiresExistingSubscription: details.requiresExistingSubscription,
      planDiscounts: [] // Initialize with empty array
    } as CouponValidationResult;
    
    // Update reference for future comparisons
    previousCouponDetailsRef.current = { ...basicDetails };
    
    // Update coupon details with basic info first
    setCouponDetails(basicDetails);
    
    // Show error message if subscription required
    if (isValid && basicDetails.requiresExistingSubscription && userData.activePlan === 'free') {
      showToast("This coupon requires an active subscription", "error");
    }
    
    // Handle specific plan applicability
    if (isValid && details.applicablePlans && details.applicablePlans.length > 0) {
      
      // For discount-type coupons, calculate discounts for all applicable plans
      if (details.discountType !== 'free_minutes') {
        // This is done asynchronously to not block UI
        setTimeout(async () => {
          try {
            // Create an array to store all plan discount calculations
            const planDiscounts: PlanDiscount[] = [];
            
            // Process each applicable plan
            for (const planId of details.applicablePlans) {
              // Skip if we're already calculating for this plan
              if (pendingCalculationsRef.current.has(planId)) {
                console.log(`Already calculating discount for plan ${planId}, skipping`);
                continue;
              }
              
              // Mark this plan as being calculated
              pendingCalculationsRef.current.add(planId);
              
              console.log(`Calculating discount for plan ${planId}`);
              
              try {
                // Calculate price with coupon for this plan
                const result = await subscriptionService.calculatePriceWithCoupon(
                  planId, 
                  details.code || ''
                );
                
                // Store the result for this plan
                planDiscounts.push({
                  planId,
                  discountedPrice: result.discountedPrice,
                  originalPrice: result.originalPrice,
                  discountAmount: result.discount,
                  freeMinutes: result.freeMinutes
                });
              } finally {
                // Always remove from pending set even if calculation failed
                pendingCalculationsRef.current.delete(planId);
              }
            }
            
            // Only update if we have new discounts to add
            if (planDiscounts.length > 0) {
              // Update the details with plan discounts using functional update to avoid stale state
              setCouponDetails(prev => {
                if (!prev) return null;
                
                // Combine existing discounts with new ones, avoiding duplicates
                const existingDiscounts = prev.planDiscounts || [];
                const updatedDiscounts = [...existingDiscounts];
                
                // Update or add new discounts
                for (const newDiscount of planDiscounts) {
                  const existingIndex = updatedDiscounts.findIndex(
                    d => d.planId === newDiscount.planId
                  );
                  
                  if (existingIndex >= 0) {
                    updatedDiscounts[existingIndex] = newDiscount;
                  } else {
                    updatedDiscounts.push(newDiscount);
                  }
                }
                
                return {
                  ...prev,
                  planDiscounts: updatedDiscounts
                };
              });
            }
          } catch (error) {
            console.error("Error calculating price with coupon:", error);
          }
        }, 100); // Slightly longer delay to ensure UI stays responsive
      }
    }
    
    // Determine if we're showing standalone coupon (free minutes with direct redemption)
    // Only show standalone option for valid coupons with free minutes
    const shouldShowStandalone = isValid && details.freeMinutes > 0;
    setShowStandaloneCoupon(shouldShowStandalone);
  };
  
  // Handle redeeming a standalone coupon
  const handleRedeemStandaloneCoupon = async () => {
    if (!couponDetails || !couponDetails.isValid) {
      return;
    }
    
    try {
      // Call backend to apply the coupon directly
      const response = await subscriptionService.redeemCouponForFreeMinutes(couponDetails.code || '');
      
      if (response.success) {
        // Show success notification
        showToast(`Coupon applied successfully! You've received ${couponDetails.freeMinutes} free minutes.`, 'success');
        
        // Clear the coupon after successful redemption
        setCouponDetails(null);
        
        // Refresh the user's subscription data
        const updatedSubscription = await subscriptionService.getUserSubscription(true);
        if (updatedSubscription && onSubscriptionUpdate) {
          onSubscriptionUpdate();
        }
      } else {
        showToast(response.message || 'Failed to apply coupon', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'An error occurred while applying the coupon', 'error');
    }
  };

  // Handle selecting a plan for coupon validation
  const handlePlanSelected = (planId: string) => {
    
    // If we have coupon details, check applicability and calculate discounts
    if (couponDetails && couponDetails.isValid) {
      // Check if coupon has plan restrictions
      if (couponDetails.applicablePlans && couponDetails.applicablePlans.length > 0) {
        // Check if selected plan is applicable for this coupon
        const isPlanApplicable = couponDetails.applicablePlans.includes(planId);
        
        if (!isPlanApplicable) {
          // If plan is not applicable, return
          return;
        }
        
        // Check if we already have discount info for this plan from our planDiscounts array
        if (couponDetails.planDiscounts && Array.isArray(couponDetails.planDiscounts)) {
          const existingPlanDiscount = couponDetails.planDiscounts.find(
            discount => discount.planId === planId
          );
          
          if (existingPlanDiscount) {
            // No need to recalculate, we already have the discount info
            console.log(`Using existing discount calculation for plan ${planId}`);
            return;
          }
        }
        
        // Skip if we're already calculating for this plan
        if (pendingCalculationsRef.current.has(planId)) {
          console.log(`Already calculating discount for plan ${planId}, skipping duplicate calculation`);
          return;
        }
        
        // Mark this plan as being calculated
        pendingCalculationsRef.current.add(planId);
        
        // Calculate discounts for the selected plan - but don't do this for every render
        setTimeout(async () => {
          try {
            // Calculate price with coupon
            const result = await subscriptionService.calculatePriceWithCoupon(
              planId,
              couponDetails.code || ''
            );
            
            // Update state with the new discount info using functional update to avoid race conditions
            setCouponDetails(prev => {
              if (!prev) return null;
              
              // Create or update planDiscounts array
              const planDiscounts = prev.planDiscounts ? [...prev.planDiscounts] : [];
              
              // Find existing discount for this plan
              const existingIndex = planDiscounts.findIndex(pd => pd.planId === planId);
              
              // Create the new discount object
              const newDiscount = {
                planId,
                discountedPrice: result.discountedPrice,
                originalPrice: result.originalPrice,
                discountAmount: result.discount,
                freeMinutes: result.freeMinutes
              };
              
              // Update or add the discount info
              if (existingIndex >= 0) {
                planDiscounts[existingIndex] = newDiscount;
              } else {
                planDiscounts.push(newDiscount);
              }
              
              // Return the updated state
              return {
                ...prev,
                planDiscounts
              };
            });
          } catch (error) {
            console.error("Error calculating price with coupon for selected plan:", error);
            showToast(`This coupon cannot be applied to the selected plan`, 'error');
          } finally {
            // Always remove from pending calculations set
            pendingCalculationsRef.current.delete(planId);
          }
        }, 100); // Longer delay to prevent UI blocking
      }
    }
  };

  // On mount, check for ?coupon=... in URL and set initialCouponCode ONCE
  useEffect(() => {
    if (initialCouponSetRef.current) return;
    const searchParams = new URLSearchParams(location.search);
    console.log("searchParams", searchParams);
    const couponParam = searchParams.get('coupon');
    if (couponParam) {
      setInitialCouponCode(couponParam.toUpperCase());
      initialCouponSetRef.current = true;
      console.log('Setting initialCouponCode from URL:', couponParam.toUpperCase());
    }
  }, [location.search]);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
            <PageTransitionItem index={1}>
              <div className="flex justify-between items-center mb-3">
                <h1 className="text-2xl md:text-3xl mt-6 font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
                  Subscription & Interview Minutes
                </h1>
              </div>
              
              <p className="text-text-secondary-light dark:text-text-secondary-dark mb-8 max-w-3xl font-medium text-base">
                Manage your subscription plan and interview minutes to get the most out of your interview practice.
              </p>
            </PageTransitionItem>

          {/* Tabs - Only show if not in onboarding */}
          {fromSource !== 'onboarding' && (
            <PageTransitionItem index={2}>
              <div className="overflow-x-auto md:overflow-visible pb-2 md:pb-0">
                <div className="inline-flex w-full md:w-auto mb-8 bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm p-1 rounded-lg min-w-full md:min-w-0">
                  <button
                    onClick={() => handleTabChange('plans')}
                    className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'plans'
                        ? 'bg-primary-light dark:bg-primary-dark text-white'
                        : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
                    }`}
                  >
                    <div className="flex items-center justify-center md:justify-start">
                      <CreditCard size={16} className="mr-1.5 flex-shrink-0" />
                      <span className="truncate">{tabLabels.plans}</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleTabChange('usage')}
                    className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'usage'
                        ? 'bg-primary-light dark:bg-primary-dark text-white'
                        : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
                    }`}
                  >
                    <div className="flex items-center justify-center md:justify-start">
                      <BarChart size={16} className="mr-1.5 flex-shrink-0" />
                      <span className="truncate">{tabLabels.usage}</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleTabChange('history')}
                    className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'history'
                        ? 'bg-primary-light dark:bg-primary-dark text-white'
                        : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
                    }`}
                  >
                    <div className="flex items-center justify-center md:justify-start">
                      <ClockIcon size={16} className="mr-1.5 flex-shrink-0" />
                      <span className="truncate">{tabLabels.history}</span>
                    </div>
                  </button>
                </div>
              </div>
            </PageTransitionItem>
          )}

          {/* Coupon input at the page level */}
          <PageTransitionItem index={4}>
          {
            activeTab === 'plans' && (
              <>
            <div className="mb-8">
              <CollegeAvatars />
            </div>

                      {/* No active plan alert - only shown if user is on free plan */}
          {userData.activePlan === 'free' && (
            <PageTransitionItem index={2}>
              <div className="mb-8 p-4 rounded-xl border border-yellow-500/20 bg-yellow-500/5 backdrop-blur-sm">
                <div className="flex flex-col md:flex-row items-start md:itms-center">
                  <div className="p-2 rounded-full bg-yellow-500/10 mr-4">
                    <AlertCircle className="w-6 h-6 text-amber-500 dark:text-amber-400" />
                  </div>
                  <div className=" mt-2 md:mt-0 flex-1 justify-start">
                    <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                      You're on the Free Plan
                    </h3>
                    <p className="mt-1 text-text-secondary-light dark:text-text-secondary-dark text-sm">
                      Upgrade to get more interview minutes and unlock premium features.
                    </p>
                  </div>
                  {fromSource === 'onboarding' && ( 
                  <Button 
                    variant="outline"
                    size='sm'
                    onClick={handleContinueWithFreePlan}
                    rightIcon={<ArrowRight size={16} />}
                    className="mt-4 md:mt-2 !text-text-secondary-light !border-secondary-500/30 dark:!border-secondary-400/30 hover:!bg-secondary-500/5"
                  >
                    Continue with Free Plan
                  </Button>
                  )}
                </div>
              </div>
            </PageTransitionItem>
          )}


            <div className="mb-8 bg-surface-light dark:bg-surface-dark p-4 md:p-6 rounded-xl border border-border-light dark:border-border-dark">
             
              {/* Location indicator for non-Indian users */}
              {!shouldShowPricing && userLocation && (
                <div className="mb-6 p-4 rounded-xl border border-blue-500/20 dark:border-blue-400/20 bg-blue-500/5 dark:bg-blue-400/5 backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-blue-500/10 dark:bg-blue-400/10">
                      <Globe className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                        Hi There!
                      </h3>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        We're expanding globally soon to support payments across the world. Till then, you can request a plan trial and we'll contact you over email for the same.
                      </p>
                    </div>
                  </div>
                </div>
              )}


              <div className="flex items-center mb-4">
                <Tag className="w-5 h-5 text-primary-light dark:text-primary-dark mr-2" />
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                  Have a coupon code?
                </h2>
              </div>
              
              <div className="flex flex-col md:flex-row md:space-x-6">
                <div className="flex-1">
                  <CouponInput
                    key={initialCouponCode || 'default'}
                    onCouponValidated={handleCouponValidated}
                    couponDetails={couponDetails}
                    onClearCoupon={() => {
                      previousCouponDetailsRef.current = null;
                      setCouponDetails(null);
                      setShowStandaloneCoupon(false);
                    }}
                    initialCouponCode={initialCouponCode}
                  />
                </div>
                
                <div className="flex-1 mt-4 md:-mt-11">
                  {couponDetails && couponDetails.isValid ? (
                    // Show coupon details when a valid coupon is applied
                    <div className="p-4 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-800 rounded-md h-full">
                      <div className="flex items-center mb-2">
                        <span className="text-base font-semibold text-green-700 dark:text-green-300">
                          Coupon Applied: {couponDetails.code}
                        </span>
                      </div>
                      
                      <div className="mt-2">
                        <ul className="space-y-2">
                          {couponDetails.description && (
                            <li className="flex items-start">
                              <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                                About this coupon: {couponDetails.description}
                              </p>
                            </li>
                          )}
                      
                      {couponDetails.freeMinutes > 0 && (
                            <li className="flex items-start">
                              <div className="flex items-center justify-center">
                              <Clock className="w-4 h-4 text-green-700 dark:text-green-300 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-green-700 dark:text-green-300">
                                Free minutes: {couponDetails.freeMinutes}
                              </p>
                              </div>
                            </li>
                        )}

                        {couponDetails.minPurchaseAmount !== undefined && couponDetails.minPurchaseAmount > 0 && (
                            <li className="flex items-start">
                              <div className="flex items-center justify-center">
                              <DollarSign className="w-4 h-4 text-green-700 dark:text-green-300 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-green-700 dark:text-green-300">
                                Minimum purchase amount: ₹{couponDetails.minPurchaseAmount}
                              </p>
                              </div>
                            </li>
                          )}
                        {/* {couponDetails.couponType === 'percentage' && couponDetails.value !== undefined && couponDetails.value > 0 && (
                            <li className="flex items-start">
                              <div className="flex items-center justify-center">
                              <Percent className="w-4 h-4 text-green-700 dark:text-green-300 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-green-700 dark:text-green-300">
                                {couponDetails.value}% off Applied on Applicable Plans
                              </p>
                              </div>
                            </li>
                          )} */}
                           {/* {couponDetails.couponType === 'fixed_amount' && couponDetails.value !== undefined && couponDetails.value > 0 && (
                            <li className="flex items-start">
                              <div className="flex items-center justify-center">
                              <DollarSign className="w-4 h-4 text-green-700 dark:text-green-300 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-green-700 dark:text-green-300">
                                Discounted Price For Applicable Plans: ₹{couponDetails.value}
                              </p>
                              </div>
                            </li>
                          )} */}
                          {/* {couponDetails.expiresAt && (
                            <li className="flex items-start">
                              <div className="flex items-center justify-center">
                              <Clock className="w-4 h-4 text-green-700 dark:text-green-300 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-green-700 dark:text-green-300">
                                Expires on {new Date(couponDetails.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                              </p>
                              </div>
                            </li>
                          )} */}
                          
                          {couponDetails && couponDetails.isValid && showStandaloneCoupon && couponDetails.freeMinutes > 0 && couponDetails.couponType === 'free_minutes' && (
                            <li className="flex items-start">
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2"
                                onClick={handleRedeemStandaloneCoupon}>
                                Redeem Free Minutes
                              </Button>
                            </li>
                          )}
                        </ul>
                      </div>
                      
                    </div>
                  ) : (
                    // Show default coupon info when no coupon is applied
                    <div className="rounded-md bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark p-4 md:pb-6 h-full flex flex-col justify-between">
                      <div>
                        <h4 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark mb-3">
                          What can you do with coupon codes?
                        </h4>
                        <ul className="space-y-2">
                          <li className="flex items-start text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <Percent className="w-4 h-4 text-primary-light dark:text-primary-dark mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">Get discounts on subscription plans</span>
                          </li>
                          <li className="flex items-start text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <Clock className="w-4 h-4 text-primary-light dark:text-primary-dark mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">Receive free interview minutes</span>
                          </li>
                          <li className="flex items-start text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <DollarSign className="w-4 h-4 text-primary-light dark:text-primary-dark mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">Save on add-on bundles</span>
                          </li>
                          <li className="flex items-start text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <X className="w-4 h-4 text-primary-light dark:text-primary-dark mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">Referral codes are not applicable as a coupon code</span>
                          </li>
                        </ul>
                      </div>
                      <div className="hidden md:block"></div> {/* Spacer for better height distribution */}
                    </div>
                  )}
                </div>
              </div>
              

            </div>
            </>
          )}
          </PageTransitionItem>
          {/* Tab Content */}
          <PageTransitionItem index={4}>
            <div>
              {/* For onboarding, only show plans */}
              {fromSource === 'onboarding' ? (
                <div>

                  <SubscriptionPlans 
                    activePlan={userData.activePlan} 
                    userSubscription={userSubscription}
                    onSubscriptionUpdate={() => {
                      if (onSubscriptionUpdate) {
                        onSubscriptionUpdate();
                      }
                      // If fromSource is onboarding, navigate to dashboard
                      if (fromSource === 'onboarding') {
                        navigate('/dashboard');
                      }
                    }}
                    couponDetails={couponDetails}
                    onPlanSelected={handlePlanSelected}
                    shouldShowPricing={shouldShowPricing}
                    userLocation={userLocation}
                  />
                  <div className="mt-0">
                    <AddOnBundles 
                      activePlan={userData.activePlan} 
                      onSubscriptionUpdate={onSubscriptionUpdate}
                      couponDetails={couponDetails}
                      onPlanSelected={handlePlanSelected}
                      shouldShowPricing={shouldShowPricing}
                      userLocation={userLocation}
                    />
                  </div>
                </div>
              ) : (
                /* For dashboard, show tab content based on active tab */
                <>
                  <div 
                    ref={plansTabRef}
                    style={{ display: activeTab === 'plans' ? 'block' : 'none' }}
                  >
                    <SubscriptionPlans 
                      activePlan={userData.activePlan} 
                      userSubscription={userSubscription}
                      onSubscriptionUpdate={onSubscriptionUpdate}
                      couponDetails={couponDetails}
                      onPlanSelected={handlePlanSelected}
                      shouldShowPricing={shouldShowPricing}
                      userLocation={userLocation}
                    />
                    
                    <div className="mt-0">
                      <AddOnBundles 
                        activePlan={userData.activePlan} 
                        onSubscriptionUpdate={onSubscriptionUpdate}
                        couponDetails={couponDetails}
                        onPlanSelected={handlePlanSelected}
                        shouldShowPricing={shouldShowPricing}
                        userLocation={userLocation}
                      />
                    </div>
                  </div>
                  
                  <div 
                    ref={usageTabRef}
                    style={{ display: activeTab === 'usage' ? 'block' : 'none' }}
                  >
                    <SubscriptionUsage 
                      userData={userData}
                      dateFilters={dateFilters}
                      selectedDateFilter={selectedDateFilter}
                      onDateFilterChange={handleDateFilterChange}
                      interviewHistoryData={interviewHistoryData}
                      isLoadingInterviewHistory={isLoadingInterviewHistory}
                      interviewHistoryError={interviewHistoryError}
                      upcomingInterviews={upcomingInterviews}
                    />
                  </div>
                  
                  <div 
                    style={{ display: activeTab === 'history' ? 'block' : 'none' }}
                  >
                    <PurchaseHistory dateFilters={dateFilters} />
                  </div>
                </>
              )}
            </div>
          </PageTransitionItem>
        </div>
      </main>
    </div>
  );
});

SubscriptionPage.displayName = 'SubscriptionPage';

export default withPageTransition(SubscriptionPage); 